import { useEffect, useRef, useState } from 'react';
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { renameScholarship } from '../../../../services/scholarships';
import { TextInput } from '../../../../components/TextInput';
import { Button } from '../../../../components/Button';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';

interface RenameTarget {
  id: string;
  name: string;
}

interface NameScholarshipModalProps {
  scholarship: RenameTarget;
  onClose: () => void;
  onSuccess: () => void;
}

export const NameScholarshipModal = ({
  scholarship,
  onClose,
  onSuccess,
}: NameScholarshipModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const [name, setName] = useState(scholarship.name);
  const [nameError, setNameError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // True only when the press that began the current click landed on the overlay
  // itself. Without this, drag-selecting text in the field and releasing over
  // the backdrop fires a click whose target resolves to the overlay, closing
  // the modal mid-selection.
  const pressedOnOverlay = useRef(false);

  useEffect(() => {
    const trigger = (document.activeElement as HTMLElement) ?? null;
    return () => {
      trigger?.focus?.();
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isSaving, onClose]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Roster name is required');
      return;
    }
    if (trimmed.length > 100) {
      setNameError('Roster name must be 100 characters or fewer');
      return;
    }

    setNameError(undefined);
    setIsSaving(true);
    setSaveError(null);

    try {
      await renameScholarship(scholarship.id, { name: trimmed });
      onSuccess();
    } catch (error) {
      setSaveError(getSafeErrorMessage(error, 'Failed to rename roster'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    pressedOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    const isOverlayClick = e.target === e.currentTarget && pressedOnOverlay.current;
    pressedOnOverlay.current = false;
    if (isOverlayClick && !isSaving) {
      onClose();
    }
  };

  return (
    <div ref={dialogRef} tabIndex={-1}
      className="scholarship-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rename-scholarship-title"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="scholarship-modal">
        <h2 id="rename-scholarship-title" className="scholarship-modal__title">
          Rename roster
        </h2>
        <form
          className="scholarship-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameError}
            disabled={isSaving}
            autoFocus
            maxLength={100}
          />
          {saveError && (
            <div className="scholarship-modal__error" role="alert">{saveError}</div>
          )}
          <div className="scholarship-modal__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
